<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import AdvisorReportsAPI from 'dashboard/api/advisorReports';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import ReportHeader from './ReportHeader.vue';
import AdvisorReportFilters from './AdvisorReportFilters.vue';
import { reportParams, pendingHours } from './advisorReportHelpers';

const DOWNLOAD_URL_LIFETIME_MS = 60_000;
const { t, locale } = useI18n();
const store = useStore();
const accountId = useMapGetter('getCurrentAccountId');
const agents = useMapGetter('agents/getAgents');
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');
const defaultFilters = () => ({
  since_date: '',
  until_date: '',
  agent_profile: '',
  author_id: '',
  assignee_id: '',
  inbox_id: '',
  team_id: '',
  label: '',
  attention_status: '',
  status: '',
  min_pending_hours: '',
  query: '',
});
const filters = ref(defaultFilters());
const applied = ref({ ...filters.value });
const summary = ref(null);
const advisors = ref([]);
const conversations = ref([]);
const detailSection = ref(null);
const pagination = ref({ total: 0, page: 1, per_page: 20 });
const sort = ref('pending_desc');
const advisorView = ref('workload');
const isLoading = ref(true);
const isDetailLoading = ref(false);
const exporting = ref('');
const error = ref('');
const exportError = ref(false);
const dirty = computed(
  () => JSON.stringify(filters.value) !== JSON.stringify(applied.value)
);
const activity = computed(() => summary.value?.activity || {});
const workload = computed(() => summary.value?.workload || {});
const profiles = computed(() =>
  (summary.value?.profiles || []).filter(
    profile => profile.agent_profile !== 'bot' || profile.total > 0
  )
);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(pagination.value.total / pagination.value.per_page))
);
const formatter = computed(() => new Intl.NumberFormat(locale.value));
const num = value => formatter.value.format(Number(value || 0));
const profileLabel = profile =>
  t(`ADVISOR_REPORT.PROFILES.${profile || 'unknown'}`);
const timezone = computed(() => summary.value?.meta?.timezone || 'UTC');
const dateText = date => {
  if (!date) return '—';
  return new Intl.DateTimeFormat(locale.value, {
    timeZone: timezone.value,
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(date));
};
const ageText = value => {
  const hours = pendingHours(value, summary.value?.meta?.generated_at);
  if (hours === null) return '—';
  if (hours < 1)
    return t('ADVISOR_REPORT.MINUTES', { count: Math.floor(hours * 60) });
  return t('ADVISOR_REPORT.HOURS', {
    count: formatter.value.format(Math.round(hours * 10) / 10),
  });
};
const metrics = computed(() => [
  {
    key: 'responses',
    value: activity.value.responses,
    icon: 'i-lucide-send',
    scope: 'PERIOD',
  },
  {
    key: 'conversations_worked',
    value: activity.value.conversations_worked,
    icon: 'i-lucide-messages-square',
    scope: 'PERIOD',
  },
  {
    key: 'unattended',
    value: workload.value.unattended,
    icon: 'i-lucide-clock-3',
    scope: 'NOW',
    attention: 'unattended',
  },
  {
    key: 'follow_up',
    value: workload.value.follow_up,
    icon: 'i-lucide-message-square-check',
    scope: 'NOW',
    attention: 'follow_up',
  },
  {
    key: 'no_exchange',
    value: workload.value.no_exchange,
    icon: 'i-lucide-message-square-dashed',
    scope: 'NOW',
    attention: 'no_exchange',
  },
  {
    key: 'external_reply',
    value: workload.value.external_reply,
    icon: 'i-lucide-contact-round',
    scope: 'NOW',
    attention: 'external_reply',
  },
]);
const activityColumns = [
  'responses',
  'conversations_worked',
  'private_notes',
  'failed',
  'delivered',
  'read',
];
const workloadColumns = [
  'total',
  'unattended',
  'follow_up',
  'no_exchange',
  'external_reply',
];
const advisorColumns = computed(() =>
  advisorView.value === 'workload' ? workloadColumns : activityColumns
);
const visibleAdvisors = computed(() => {
  if (advisorView.value !== 'workload') return advisors.value;
  return [...advisors.value].sort(
    (a, b) =>
      b.unattended - a.unattended ||
      b.total - a.total ||
      a.name.localeCompare(b.name)
  );
});
const percentage = value =>
  workload.value.total
    ? Math.round((Number(value || 0) / workload.value.total) * 100)
    : 0;
const profileSelected = profile =>
  ['unassigned', 'bot'].includes(profile)
    ? applied.value.assignee_id === profile
    : applied.value.agent_profile === profile;
let requestId = 0;
let detailId = 0;
const queryParams = () =>
  reportParams({
    ...applied.value,
    sort: sort.value,
    per_page: pagination.value.per_page,
  });
const showError = err => {
  error.value =
    err?.response?.status === 422
      ? t('ADVISOR_REPORT.INVALID_FILTERS')
      : t('ADVISOR_REPORT.ERROR');
};
const fetchReport = async () => {
  const draftPeriod = {
    since_date: filters.value.since_date,
    until_date: filters.value.until_date,
  };
  requestId += 1;
  const current = requestId;
  detailId += 1;
  isDetailLoading.value = false;
  isLoading.value = true;
  error.value = '';
  exportError.value = false;
  try {
    const params = { ...queryParams(), page: 1 };
    const [summaryResponse, advisorResponse, conversationResponse] =
      await Promise.all([
        AdvisorReportsAPI.summary(params),
        AdvisorReportsAPI.advisors(params),
        AdvisorReportsAPI.conversations(params),
      ]);
    if (current !== requestId) return;
    summary.value = summaryResponse.data;
    // Let the server choose today's calendar dates in the account timezone.
    if (!applied.value.since_date && !applied.value.until_date) {
      const defaults = {
        since_date: summary.value.meta.since_date,
        until_date: summary.value.meta.until_date,
      };
      applied.value = { ...applied.value, ...defaults };
      if (
        filters.value.since_date === draftPeriod.since_date &&
        filters.value.until_date === draftPeriod.until_date
      ) {
        filters.value = { ...filters.value, ...defaults };
      }
    }
    advisors.value = advisorResponse.data.advisors;
    conversations.value = conversationResponse.data.conversations;
    pagination.value = conversationResponse.data.meta;
  } catch (err) {
    if (current !== requestId) return;
    summary.value = null;
    advisors.value = [];
    conversations.value = [];
    showError(err);
  } finally {
    if (current === requestId) isLoading.value = false;
  }
};
const applyFilters = () => {
  applied.value = { ...filters.value };
  return fetchReport();
};
const openAdvisorDetail = async (advisor, key) => {
  filters.value = {
    ...applied.value,
    assignee_id: advisor.id,
    attention_status: key === 'total' ? applied.value.attention_status : key,
  };
  await applyFilters();
  await nextTick();
  detailSection.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
};
const reset = () => {
  filters.value = defaultFilters();
  applyFilters();
};
const narrow = (key, value) => {
  if (!value) return;
  filters.value = {
    ...applied.value,
    [key]: applied.value[key] === value ? '' : value,
  };
  applyFilters();
};
const narrowProfile = profile => {
  const selected = profileSelected(profile);
  const nonHuman = ['unassigned', 'bot'].includes(profile);
  filters.value = {
    ...applied.value,
    agent_profile: nonHuman || selected ? '' : profile,
    assignee_id: nonHuman && !selected ? profile : '',
  };
  applyFilters();
};
const fetchPage = async page => {
  detailId += 1;
  const current = detailId;
  isDetailLoading.value = true;
  error.value = '';
  try {
    const { data } = await AdvisorReportsAPI.conversations({
      ...queryParams(),
      page,
    });
    if (current !== detailId) return;
    conversations.value = data.conversations;
    pagination.value = data.meta;
  } catch (err) {
    if (current === detailId) showError(err);
  } finally {
    if (current === detailId) isDetailLoading.value = false;
  }
};
const download = async kind => {
  const params = { ...queryParams(), kind };
  exporting.value = kind;
  exportError.value = false;
  try {
    const { data } = await AdvisorReportsAPI.export(params);
    const fileName = `cofae-${kind}-${params.since_date}-${params.until_date}.csv`;
    const link = downloadCsvFile(fileName, data);
    // A download may start asynchronously after the click, especially in WebKit.
    setTimeout(() => URL.revokeObjectURL(link.href), DOWNLOAD_URL_LIFETIME_MS);
  } catch {
    exportError.value = true;
  } finally {
    exporting.value = '';
  }
};
onMounted(() => {
  ['agents/get', 'inboxes/get', 'teams/get', 'labels/get'].forEach(action =>
    store.dispatch(action)
  );
  fetchReport();
});
</script>

<template>
  <div data-testid="advisor-management" class="pb-6">
    <ReportHeader
      :header-title="t('ADVISOR_REPORT.TITLE')"
      :header-description="t('ADVISOR_REPORT.DESCRIPTION')"
    >
      <Button
        :label="t('ADVISOR_REPORT.REFRESH')"
        icon="i-lucide-refresh-cw"
        variant="outline"
        color="slate"
        :disabled="isLoading"
        @click="fetchReport"
      />
    </ReportHeader>
    <AdvisorReportFilters
      v-model="filters"
      :agents="agents"
      :inboxes="inboxes"
      :teams="teams"
      :labels="labels"
      :busy="isLoading"
      @apply="applyFilters"
      @reset="reset"
    />
    <div
      class="flex flex-wrap items-center justify-between gap-2 my-4 text-xs text-n-slate-11"
    >
      <span v-if="summary">{{
        t('ADVISOR_REPORT.UPDATED', {
          date: dateText(summary.meta.generated_at),
          timezone,
        })
      }}</span>
      <span v-if="dirty" class="text-n-amber-11">{{
        t('ADVISOR_REPORT.UNAPPLIED')
      }}</span>
    </div>
    <div
      v-if="error"
      role="alert"
      class="p-4 mb-5 rounded-xl border border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
    >
      {{ error }}
    </div>
    <div
      v-if="exportError"
      role="alert"
      class="p-3 mb-4 rounded-lg bg-n-ruby-2 text-n-ruby-11"
    >
      {{ t('ADVISOR_REPORT.EXPORT_ERROR') }}
    </div>
    <div
      v-if="isLoading"
      role="status"
      class="flex items-center justify-center gap-3 py-24 text-n-slate-11"
    >
      <Spinner />{{ t('ADVISOR_REPORT.LOADING') }}
    </div>
    <div v-else-if="summary" class="flex flex-col gap-6">
      <div class="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <component
          :is="metric.attention ? 'button' : 'div'"
          v-for="metric in metrics"
          :key="metric.key"
          :type="metric.attention ? 'button' : undefined"
          class="rounded-xl border border-n-weak bg-n-solid-1 p-4 transition-colors text-start"
          :aria-pressed="
            metric.attention
              ? applied.attention_status === metric.attention
              : undefined
          "
          :class="{
            'ring-1 ring-n-brand':
              metric.attention && applied.attention_status === metric.attention,
            'hover:border-n-strong hover:bg-n-alpha-black2': metric.attention,
            'cursor-default': !metric.attention,
          }"
          @click="narrow('attention_status', metric.attention)"
        >
          <div class="flex justify-between items-center gap-2 text-n-slate-11">
            <span class="text-xs">{{
              t(`ADVISOR_REPORT.${metric.scope}`)
            }}</span
            ><span :class="metric.icon" class="size-4" aria-hidden="true" />
          </div>
          <strong
            class="block mt-3 text-3xl font-semibold tracking-tight tabular-nums"
            :class="
              metric.key === 'unattended' ? 'text-n-ruby-11' : 'text-n-slate-12'
            "
            >{{ num(metric.value) }}</strong
          >
          <span class="block mt-1 text-sm text-n-slate-11">{{
            t(`ADVISOR_REPORT.METRICS.${metric.key}`)
          }}</span>
        </component>
      </div>

      <section
        class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-n-weak"
        >
          <div>
            <h2 class="text-base font-semibold text-n-slate-12 m-0">
              {{ t('ADVISOR_REPORT.WORKLOAD_TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-11 mt-1 mb-0 max-w-xl">
              {{ t('ADVISOR_REPORT.WORKLOAD_HINT') }}
            </p>
          </div>
          <span class="text-xs text-n-slate-11">{{
            t('ADVISOR_REPORT.CASES', { count: num(workload.total) })
          }}</span>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 p-4">
          <button
            v-for="profile in profiles"
            :key="profile.agent_profile"
            type="button"
            class="p-4 rounded-lg bg-n-alpha-black2 border border-n-weak text-start transition-colors hover:border-n-strong"
            :class="{
              'ring-1 ring-n-brand': profileSelected(profile.agent_profile),
            }"
            :aria-pressed="profileSelected(profile.agent_profile)"
            :aria-label="
              t('ADVISOR_REPORT.FILTER_PROFILE', {
                profile: profileLabel(profile.agent_profile),
              })
            "
            @click="narrowProfile(profile.agent_profile)"
          >
            <div class="flex justify-between items-center gap-2">
              <span class="text-sm font-medium text-n-slate-12">{{
                profileLabel(profile.agent_profile)
              }}</span
              ><strong class="text-xl tabular-nums text-n-slate-12">{{
                num(profile.total)
              }}</strong>
            </div>
            <span class="block mt-1 text-xs text-n-slate-11 tabular-nums">
              {{
                t('ADVISOR_REPORT.SHARE', {
                  percent: percentage(profile.total),
                })
              }}
            </span>
            <svg
              class="w-full h-1.5 mt-3"
              viewBox="0 0 100 6"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <rect width="100" height="6" rx="3" class="fill-n-slate-4" />
              <rect
                :width="
                  workload.total
                    ? Math.round((profile.total / workload.total) * 100)
                    : 0
                "
                height="6"
                rx="3"
                class="fill-n-brand"
              />
            </svg>
            <div
              class="flex justify-between gap-2 text-xs mt-3 text-n-slate-11"
            >
              <span>{{
                t('ADVISOR_REPORT.PENDING_COUNT', {
                  count: num(profile.unattended),
                })
              }}</span
              ><span>{{
                t('ADVISOR_REPORT.FOLLOW_COUNT', {
                  count: num(profile.follow_up),
                })
              }}</span>
            </div>
          </button>
        </div>
      </section>

      <section
        class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-n-weak"
        >
          <div>
            <h2 class="text-base font-semibold text-n-slate-12 m-0">
              {{ t('ADVISOR_REPORT.ADVISORS_TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-11 mt-1 mb-0 max-w-xl">
              {{
                t(
                  advisorView === 'workload'
                    ? 'ADVISOR_REPORT.WORKLOAD_TABLE_HINT'
                    : 'ADVISOR_REPORT.ACTIVITY_HINT'
                )
              }}
            </p>
          </div>
          <Button
            :label="t('ADVISOR_REPORT.EXPORT_ADVISORS')"
            icon="i-lucide-download"
            variant="ghost"
            color="slate"
            size="sm"
            :disabled="!!exporting"
            :is-loading="exporting === 'advisors'"
            @click="download('advisors')"
          />
        </div>
        <div
          class="flex gap-2 p-4 border-b border-n-weak"
          role="group"
          :aria-label="t('ADVISOR_REPORT.VIEW')"
        >
          <Button
            v-for="view in ['workload', 'activity']"
            :key="view"
            size="sm"
            color="slate"
            :variant="advisorView === view ? 'solid' : 'ghost'"
            :label="t(`ADVISOR_REPORT.VIEWS.${view}`)"
            :aria-pressed="advisorView === view"
            @click="advisorView = view"
          />
        </div>
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-start border-collapse">
            <caption class="sr-only">
              {{
                t('ADVISOR_REPORT.ADVISORS_TITLE')
              }}
            </caption>
            <thead class="bg-n-alpha-black2 text-n-slate-11 text-xs">
              <tr>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.ADVISOR') }}
                </th>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.PROFILE') }}
                </th>
                <th
                  v-for="key in advisorColumns"
                  :key="key"
                  scope="col"
                  class="px-4 whitespace-nowrap py-3 font-medium text-end tabular-nums"
                  :class="{ 'bg-n-alpha-black2': advisorView === 'workload' }"
                >
                  {{ t(`ADVISOR_REPORT.METRICS.${key}`) }}
                </th>
              </tr>
            </thead>
            <tbody
              class="[&>tr]:border-t [&>tr]:border-n-weak [&>tr]:text-n-slate-12 [&>tr:hover]:bg-n-alpha-black2"
            >
              <tr
                v-for="advisor in visibleAdvisors"
                :key="advisor.id ?? 'unassigned'"
              >
                <th scope="row" class="px-4 whitespace-nowrap py-3 font-medium">
                  {{ advisor.name }}
                </th>
                <td class="px-4 whitespace-nowrap py-3">
                  <span
                    class="inline-flex text-xs px-2 py-0.5 rounded-md bg-n-alpha-black2 text-n-slate-11"
                    >{{ profileLabel(advisor.agent_profile) }}</span
                  >
                </td>
                <td
                  v-for="key in advisorColumns"
                  :key="key"
                  class="px-4 whitespace-nowrap py-3 text-end tabular-nums"
                  :class="{
                    'bg-n-alpha-black2': advisorView === 'workload',
                    'text-n-ruby-11':
                      ['unattended', 'failed'].includes(key) && advisor[key],
                  }"
                >
                  <button
                    v-if="
                      advisorView === 'workload' &&
                      advisor.agent_profile &&
                      Number(advisor[key]) > 0
                    "
                    type="button"
                    class="rounded-md px-2 py-1 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
                    :class="
                      key === 'unattended' && advisor[key]
                        ? 'text-n-ruby-11'
                        : 'text-n-blue-11'
                    "
                    :aria-label="
                      t('ADVISOR_REPORT.OPEN_ADVISOR_DETAIL', {
                        advisor: advisor.name,
                        status: t(`ADVISOR_REPORT.METRICS.${key}`),
                      })
                    "
                    @click="openAdvisorDetail(advisor, key)"
                  >
                    {{ num(advisor[key]) }}
                  </button>
                  <span v-else>{{ num(advisor[key]) }}</span>
                </td>
              </tr>
              <tr v-if="!advisors.length">
                <td
                  :colspan="2 + advisorColumns.length"
                  class="px-4 whitespace-nowrap py-10 text-center text-n-slate-11"
                >
                  {{ t('ADVISOR_REPORT.EMPTY_ADVISORS') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="text-xs text-n-slate-11 px-4 py-3 m-0 border-t border-n-weak">
          {{
            t(
              advisorView === 'workload'
                ? 'ADVISOR_REPORT.WORKLOAD_TABLE_HINT'
                : 'ADVISOR_REPORT.DELIVERY_HINT'
            )
          }}
        </p>
      </section>

      <section
        ref="detailSection"
        class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <div
          class="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-n-weak"
        >
          <div>
            <h2 class="text-base font-semibold text-n-slate-12 m-0">
              {{ t('ADVISOR_REPORT.DETAIL_TITLE') }}
            </h2>
            <p class="text-xs text-n-slate-11 mt-1 mb-0 max-w-xl">
              {{ t('ADVISOR_REPORT.DETAIL_HINT') }}
            </p>
          </div>
          <Button
            :label="t('ADVISOR_REPORT.EXPORT_CASES')"
            icon="i-lucide-download"
            variant="ghost"
            color="slate"
            size="sm"
            :disabled="!!exporting"
            :is-loading="exporting === 'conversations'"
            @click="download('conversations')"
          />
        </div>
        <div
          class="flex items-center justify-between gap-3 p-4 border-b border-n-weak"
        >
          <span class="text-sm text-n-slate-11">{{
            t('ADVISOR_REPORT.CASES', { count: num(pagination.total) })
          }}</span
          ><select
            v-model="sort"
            class="w-auto max-w-[65%] text-sm h-9 mb-0 rounded-lg border-n-weak bg-n-alpha-black2 text-n-slate-12"
            :aria-label="t('ADVISOR_REPORT.SORT')"
            :disabled="isDetailLoading"
            @change="fetchPage(1)"
          >
            <option value="pending_desc">
              {{ t('ADVISOR_REPORT.SORT_PENDING') }}
            </option>
            <option value="latest_desc">
              {{ t('ADVISOR_REPORT.SORT_LATEST') }}
            </option>
          </select>
        </div>
        <div
          :aria-busy="isDetailLoading"
          class="overflow-x-auto"
          :class="{ 'opacity-50 pointer-events-none': isDetailLoading }"
        >
          <table class="w-full text-sm text-start border-collapse">
            <caption class="sr-only">
              {{
                t('ADVISOR_REPORT.DETAIL_TITLE')
              }}
            </caption>
            <thead class="bg-n-alpha-black2 text-n-slate-11 text-xs">
              <tr>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.CONVERSATION') }}
                </th>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.OWNER') }}
                </th>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.ATTENTION_TITLE') }}
                </th>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.PENDING_SINCE') }}
                </th>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.LAST_EXCHANGE') }}
                </th>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.INBOX_TEAM') }}
                </th>
                <th class="px-4 whitespace-nowrap py-3 font-medium" scope="col">
                  {{ t('ADVISOR_REPORT.LABELS') }}
                </th>
              </tr>
            </thead>
            <tbody
              class="[&>tr]:border-t [&>tr]:border-n-weak [&>tr]:text-n-slate-12 [&>tr:hover]:bg-n-alpha-black2"
            >
              <tr v-for="conversation in conversations" :key="conversation.id">
                <td class="px-4 whitespace-nowrap py-3">
                  <router-link
                    :to="`/app/accounts/${accountId}/conversations/${conversation.display_id}`"
                    class="text-n-blue-11 hover:underline font-medium"
                  >
                    #{{ conversation.display_id }} ·
                    {{ conversation.contact_name }} </router-link
                  ><span class="block text-xs text-n-slate-10 mt-1">{{
                    t(`ADVISOR_REPORT.STATUS.${conversation.status}`)
                  }}</span>
                </td>
                <td class="px-4 whitespace-nowrap py-3">
                  <span class="block">{{
                    conversation.assignee_name ||
                    t('ADVISOR_REPORT.PROFILES.unassigned')
                  }}</span
                  ><span class="text-xs text-n-slate-10">{{
                    profileLabel(conversation.agent_profile)
                  }}</span>
                </td>
                <td class="px-4 whitespace-nowrap py-3">
                  <span
                    class="inline-flex rounded-md px-2 py-1 text-xs font-medium"
                    :class="
                      conversation.attention_status === 'unattended'
                        ? 'bg-n-ruby-2 text-n-ruby-11'
                        : 'bg-n-alpha-black2 text-n-slate-11'
                    "
                    >{{
                      t(
                        `ADVISOR_REPORT.ATTENTION.${conversation.attention_status}`
                      )
                    }}</span
                  >
                </td>
                <td
                  class="px-4 whitespace-nowrap py-3 tabular-nums"
                  :title="dateText(conversation.pending_since)"
                >
                  {{ ageText(conversation.pending_since) }}
                </td>
                <td
                  class="px-4 whitespace-nowrap py-3 text-n-slate-11 tabular-nums"
                >
                  {{ dateText(conversation.last_exchange_at) }}
                </td>
                <td class="px-4 whitespace-nowrap py-3">
                  <span class="block">{{ conversation.inbox_name }}</span
                  ><span class="text-xs text-n-slate-10">{{
                    conversation.team_name || '—'
                  }}</span>
                </td>
                <td class="px-4 whitespace-nowrap py-3">
                  <div class="flex flex-wrap gap-1 max-w-48">
                    <span
                      v-for="label in conversation.labels"
                      :key="label"
                      class="inline-flex text-xs px-2 py-0.5 rounded-md bg-n-alpha-black2 text-n-slate-11"
                      >{{ label }}</span
                    ><span v-if="!conversation.labels?.length">—</span>
                  </div>
                </td>
              </tr>
              <tr v-if="!conversations.length">
                <td
                  colspan="7"
                  class="px-4 whitespace-nowrap py-10 text-center text-n-slate-11"
                >
                  <span class="i-lucide-search-x block size-6 mx-auto mb-2" />{{
                    t('ADVISOR_REPORT.EMPTY_CASES')
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          class="flex items-center justify-between gap-3 p-4 border-t border-n-weak"
        >
          <span class="text-xs text-n-slate-11">{{
            t('ADVISOR_REPORT.PAGE', {
              page: pagination.page,
              total: totalPages,
            })
          }}</span>
          <div class="flex gap-2">
            <Button
              variant="outline"
              color="slate"
              size="sm"
              :label="t('ADVISOR_REPORT.PREVIOUS')"
              :disabled="pagination.page <= 1 || isDetailLoading"
              @click="fetchPage(pagination.page - 1)"
            /><Button
              variant="outline"
              color="slate"
              size="sm"
              :label="t('ADVISOR_REPORT.NEXT')"
              :disabled="pagination.page >= totalPages || isDetailLoading"
              @click="fetchPage(pagination.page + 1)"
            />
          </div>
        </div>
      </section>
      <details
        class="rounded-xl border border-n-weak p-4 text-sm text-n-slate-11"
      >
        <summary class="cursor-pointer font-medium text-n-slate-12">
          {{ t('ADVISOR_REPORT.DEFINITIONS') }}
        </summary>
        <div class="grid sm:grid-cols-2 gap-4 mt-4">
          <p class="m-0">{{ t('ADVISOR_REPORT.DEFINITION_ATTENTION') }}</p>
          <p class="m-0">{{ t('ADVISOR_REPORT.DEFINITION_ACTIVITY') }}</p>
        </div>
      </details>
    </div>
  </div>
</template>
